import { useEffect, useRef } from 'react';
import { siteConfig } from '../lib/config';

/** Ảnh chụp hũ đẩy realtime (mỗi lần jackpot-client thấy giá trị đổi). */
export interface JackpotStateEvent {
  type: 'state';
  status: 'connecting' | 'connected' | 'disconnected';
  updatedAt: string;
  games: { game: 'tx' | 'xd' | 'tl'; name?: string; jackpot: number }[];
}

/** Một hũ vừa NỔ. */
export interface JackpotWinEvent {
  type: 'win';
  game: 'tx' | 'xd' | 'tl';
  name?: string;
  value: number;
  at: number;
}

export type JackpotEvent = JackpotStateEvent | JackpotWinEvent;

/**
 * Lắng nghe SSE stream `/api/v1/jackpot/events` từ server.
 *
 * Server subscribe Redis channel mà jackpot-client (worker) publish lên mỗi khi
 * giá trị hũ đổi (`type:'state'`) hoặc có hũ nổ (`type:'win'`), forward về đây.
 * Hũ là dữ liệu CHUNG (broadcast) — server KHÔNG lọc theo userId.
 *
 * - Tự reconnect khi connection đóng (backoff 1s → 30s), như `useSyncEvents`.
 * - Auth qua cookie httpOnly — gửi kèm nhờ `withCredentials: true`.
 * - Callback giữ trong ref để đổi handler không phải đăng ký lại.
 *
 * @param onEvent callback khi nhận 1 event hũ (state / win).
 * @param enabled nếu false thì không mở connection (vd widget đang ẩn).
 */
export function useJackpotEvents(onEvent: (event: JackpotEvent) => void, enabled = true): void {
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    if (!enabled) return;

    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;
    let retryDelay = 1000;

    const connect = (): void => {
      if (closed) return;
      const url = `${siteConfig.apiUrl}/api/v1/jackpot/events`;
      es = new EventSource(url, { withCredentials: true });

      es.addEventListener('jackpot', (e: MessageEvent) => {
        try {
          cbRef.current(JSON.parse(e.data as string) as JackpotEvent);
        } catch {
          // Bỏ qua frame hỏng định dạng.
        }
      });

      es.onopen = () => {
        retryDelay = 1000; // reset backoff khi kết nối lại thành công
      };

      es.onerror = () => {
        es?.close();
        es = null;
        if (closed) return;
        // Backoff exponential: 1s → 2s → 4s → ... tối đa 30s
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 30_000);
      };
    };

    connect();

    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      es?.close();
      es = null;
    };
  }, [enabled]);
}
